import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { eventService } from '../services/eventService';
import { extractApiErrorMessage } from '../utils/apiErrors';

export default function useEventEditorActions({
  canManage = false,
  onEventCreated,
  onEventUpdated,
  onEventDeleted,
  refreshEvents,
  getMutationScope,
  logLabel = 'event',
} = {}) {
  const { t } = useTranslation();
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [eventModalMode, setEventModalMode] = useState('create');
  const [eventBeingEdited, setEventBeingEdited] = useState(null);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [isDeletingEvent, setIsDeletingEvent] = useState(false);
  const [eventDeleteError, setEventDeleteError] = useState(null);

  const resetEventUiState = useCallback(() => {
    setIsEventModalOpen(false);
    setEventModalMode('create');
    setEventBeingEdited(null);
    setEventToDelete(null);
    setIsDeletingEvent(false);
    setEventDeleteError(null);
  }, []);

  useEffect(() => {
    if (!canManage) {
      resetEventUiState();
    }
  }, [canManage, resetEventUiState]);

  const currentMutationScope = useCallback(() => (
    typeof getMutationScope === 'function' ? getMutationScope() : null
  ), [getMutationScope]);

  const isCurrentMutationScope = useCallback((scope) => (
    typeof getMutationScope !== 'function' || Object.is(currentMutationScope(), scope)
  ), [currentMutationScope, getMutationScope]);

  const refreshAfterMutation = useCallback(() => {
    if (typeof refreshEvents === 'function') {
      refreshEvents();
    }
  }, [refreshEvents]);

  const handleEventCreated = useCallback((event) => {
    if (typeof onEventCreated === 'function') {
      onEventCreated(event);
    }
    refreshAfterMutation();
  }, [onEventCreated, refreshAfterMutation]);

  const handleEventUpdated = useCallback((event) => {
    if (typeof onEventUpdated === 'function') {
      onEventUpdated(event);
    }
    refreshAfterMutation();
  }, [onEventUpdated, refreshAfterMutation]);

  const handleEventDeleted = useCallback((event) => {
    if (typeof onEventDeleted === 'function') {
      onEventDeleted(event);
    }
    refreshAfterMutation();
  }, [onEventDeleted, refreshAfterMutation]);

  const openCreateEvent = useCallback(() => {
    if (!canManage) return;
    setEventModalMode('create');
    setEventBeingEdited(null);
    setIsEventModalOpen(true);
  }, [canManage]);

  const openEditEvent = useCallback((event) => {
    if (!canManage || !event?.id) return;
    setEventModalMode('edit');
    setEventBeingEdited(event);
    setIsEventModalOpen(true);
  }, [canManage]);

  const closeEventModal = useCallback(() => {
    setIsEventModalOpen(false);
    setEventModalMode('create');
    setEventBeingEdited(null);
  }, []);

  const requestDeleteEvent = useCallback((event) => {
    if (!canManage || !event?.id) return;
    setEventToDelete(event);
    setEventDeleteError(null);
  }, [canManage]);

  const cancelDeleteEvent = useCallback(() => {
    if (isDeletingEvent) return;
    setEventToDelete(null);
    setEventDeleteError(null);
  }, [isDeletingEvent]);

  const confirmDeleteEvent = useCallback(async () => {
    if (!canManage || !eventToDelete?.id || isDeletingEvent) return;
    const mutationScope = currentMutationScope();
    setIsDeletingEvent(true);
    setEventDeleteError(null);
    try {
      await eventService.deleteEvent(eventToDelete.id);
      if (!isCurrentMutationScope(mutationScope)) return;
      setEventToDelete(null);
      handleEventDeleted(eventToDelete);
    } catch (error) {
      if (!isCurrentMutationScope(mutationScope)) return;
      console.error(`Failed to delete ${logLabel}`, error);
      setEventDeleteError(extractApiErrorMessage(
        error,
        t('eventsPage.editModal.errors.delete'),
      ));
    } finally {
      setIsDeletingEvent(false);
    }
  }, [
    canManage,
    currentMutationScope,
    eventToDelete,
    handleEventDeleted,
    isCurrentMutationScope,
    isDeletingEvent,
    logLabel,
    t,
  ]);

  return {
    isEventModalOpen,
    eventModalMode,
    eventBeingEdited,
    eventToDelete,
    isDeletingEvent,
    eventDeleteError,
    resetEventUiState,
    openCreateEvent,
    openEditEvent,
    closeEventModal,
    handleEventCreated,
    handleEventUpdated,
    handleEventDeleted,
    requestDeleteEvent,
    cancelDeleteEvent,
    confirmDeleteEvent,
  };
}
